import { contractTypesQueryOptions } from '@/shared/contract-types-query';
import type { PostJobsMutationRequest } from '@eureca/recruiter-api-sdk/types';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { revalidateLogic, useForm, useFormErrors, useStore } from '@eureca/ui/forms';
import { toast, type ComboboxOption } from '@eureca/ui';
import { z } from 'zod';
import {
  JobCreatePage as JobCreatePageComponent,
  type JobCreateFormat,
  type JobCreateStep,
  type JobCreateValues,
} from './JobCreatePage.component';
import { jobProgramOptionsQueryOptions } from './job-program-options-query';
import { JobMutationError, usePostJob } from './job-create-mutation';
import { programByIdQueryOptions } from '@/features/programs/program-edit/program-by-id-query';
import { DEFAULT_JOBS_SEARCH } from '@/features/jobs/jobs-list/jobs-search.schema';
import { getSelectedWorkspaceId } from '@/router/require-workspace';
import {
  confirmJobImport,
  downloadImportConflicts,
  downloadImportTemplate,
  uploadJobImport,
  type PreparedImport,
} from './job-import-api';
import {
  JOB_IMPORT_MESSAGES,
  confirmErrorMessage,
  fileListError,
  localFileError,
  isRetryableUploadError,
  uploadErrorMessage,
} from './job-import-errors';

type JobContractTypeToken = NonNullable<PostJobsMutationRequest['contractType']>;

export const NAME_TAKEN_MESSAGE = 'Já existe uma vaga com esse nome no programa selecionado';
export const GENERIC_ERROR_TOAST = 'Algo deu errado. Tente novamente';
/** Falha ao carregar a lista de programas (erro de API ≠ carregando ≠ vazio). */
export const PROGRAMS_LOAD_ERROR_MESSAGE = JOB_IMPORT_MESSAGES.server;

const schema = z
  .object({
    programId: z.string().uuid('Selecione o programa'),
    format: z.enum(['', 'individual', 'mass']),
    name: z.string(),
    contractType: z.string(),
  })
  .superRefine((value, ctx) => {
    if (value.format === 'individual') {
      if (value.name.trim() === '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['name'],
          message: 'Informe o nome da vaga',
        });
      }
      if (value.contractType === '') {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['contractType'],
          message: 'Selecione o tipo de vaga',
        });
      }
    }
  });

/**
 * DEV-276 — container de "Criação de Vaga": o formulário (programa, formato,
 * nome, tipo) + o arquivo + a máquina de estados do caminho em massa
 * (form → confirm → success). A classificação, as contagens e a gravação são
 * do backend (AC-29); aqui só se exibe o que chega.
 */
export default function JobCreatePage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const contractTypesQuery = useQuery(contractTypesQueryOptions());
  const contractTypeOptions = useMemo(
    () =>
      (contractTypesQuery.data ?? [])
        .filter((t): t is typeof t & { token: string } => t.token !== null)
        .map((t) => ({ value: t.token, label: t.label })),
    [contractTypesQuery.data]
  );
  // AC-02 / CVM-035 — só os programas da área de trabalho atual (a empresa em
  // que a consultora entrou pela Home); o BFF filtra por `companyId`.
  const programsQuery = useQuery(jobProgramOptionsQueryOptions(getSelectedWorkspaceId()));
  const postJob = usePostJob();

  // AC-02 — só Ativo, Ativo e priv. e Rascunho (encerrados/cancelados somem).
  // A empresa já veio filtrada do BFF: a lista é exatamente o que ele devolve.
  const programOptions: ComboboxOption[] = useMemo(() => {
    const items = programsQuery.data?.items ?? [];
    return items
      .filter((p) => p.statusKey !== 'cancelled' && p.statusKey !== 'inactivated')
      .map((p) => ({ value: p.id, label: p.name }));
  }, [programsQuery.data]);

  // `retryable` (DEV-509 AC-2): o erro não é do arquivo — o mesmo arquivo serve para
  // tentar de novo e "Criar vagas" continua habilitado.
  const [file, setFile] = useState<{
    value: File | null;
    error: string | null;
    retryable?: boolean;
  }>({
    value: null,
    error: null,
  });
  const [step, setStep] = useState<JobCreateStep>({ kind: 'form' });
  const [batch, setBatch] = useState<PreparedImport | null>(null);
  const [nameTaken, setNameTaken] = useState<string | null>(null);

  const form = useForm({
    defaultValues: {
      programId: '',
      format: '' as JobCreateFormat,
      name: '',
      contractType: '',
    } satisfies JobCreateValues,
    validationLogic: revalidateLogic(),
    validators: { onDynamic: schema },
    onSubmit: async ({ value }) => {
      if (value.format === 'individual') await submitIndividual(value);
      else if (value.format === 'mass') await submitMass(value.programId);
    },
  });

  const values = useStore(form.store, (state) => state.values);
  const isSubmitting = useStore(form.store, (state) => state.isSubmitting);
  const errors = useFormErrors(form);

  const isValidProgramUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
    values.programId
  );
  const programDetailQuery = useQuery({
    ...programByIdQueryOptions(values.programId),
    enabled: isValidProgramUuid,
  });
  const companyLogoUrl = programDetailQuery.data?.company?.logoUrl ?? null;
  const companyName = programDetailQuery.data?.company?.name ?? null;
  const programName =
    programOptions.find((p) => p.value === values.programId)?.label ??
    programDetailQuery.data?.name ??
    '';

  async function submitIndividual(value: JobCreateValues) {
    try {
      // A empresa vem do detalhe do programa, que carrega depois da escolha do programa. Em
      // rede lenta o clique chega antes dele: aguarda a leitura em vez de desistir do envio.
      const companyId =
        programDetailQuery.data?.companyId ??
        (await queryClient.fetchQuery(programByIdQueryOptions(value.programId)).catch(() => null))
          ?.companyId;
      if (!companyId) {
        toast.error('Aguarde a empresa do programa carregar e tente novamente.');
        return;
      }
      const created = await postJob.mutateAsync({
        name: value.name.trim(),
        programId: value.programId,
        companyId,
        contractType: value.contractType as JobContractTypeToken,
      });
      // AC-11 (CVM-008/036) — direto ao formulário de edição da vaga recém-criada:
      // o mesmo destino do "Continuar editando" da tela de sucesso antiga
      // (`/vagas/$jobId`, que absorveu `/vagas/$jobId/editar` na EU-560).
      await navigate({ to: '/vagas/$jobId', params: { jobId: created.id } });
    } catch (err) {
      const status = err instanceof JobMutationError ? err.status : 0;
      if (status === 422) {
        // AC-10 — nome já usado no programa: o campo entra em erro, a tela fica
        // e o botão desabilita até o nome mudar (AC-06).
        setNameTaken(NAME_TAKEN_MESSAGE);
      } else if (status === 404) {
        toast.error('Programa não encontrado ou fora do seu escopo.');
      } else if (status === 401 || status === 403) {
        toast.error('Você não tem permissão para criar vagas.');
      } else {
        toast.error('Não foi possível criar a vaga. Tente novamente.');
      }
    }
  }

  // DEV-509 AC-1 — sem rede, FALHAR, não pausar (mesmo motivo do MAP-086 em
  // job-edit-mutation): no padrão (`networkMode: 'online'`) o TanStack Query estaciona a
  // mutation enquanto o navegador está offline, e a Confirmação ficava carregando para
  // sempre, com o "Voltar" travado e sem mensagem. Falhando na hora, a pessoa volta à tela
  // de envio com "Sem conexão no momento…" e tenta de novo pelo mesmo botão.
  const upload = useMutation({
    networkMode: 'always',
    mutationFn: (input: { programId: string; file: File; signal: AbortSignal }) =>
      uploadJobImport(input.programId, input.file, input.signal),
  });
  const confirm = useMutation({
    networkMode: 'always',
    mutationFn: (batchId: string) => confirmJobImport(batchId),
  });

  // Envio em andamento. Voltar, trocar de formato ou sair da tela aborta o
  // request; a ref também diz se a resposta que chegou ainda é a esperada.
  const uploadAbortRef = useRef<AbortController | null>(null);
  function cancelUpload() {
    const controller = uploadAbortRef.current;
    uploadAbortRef.current = null;
    controller?.abort();
  }
  useEffect(
    () => () => {
      uploadAbortRef.current?.abort();
    },
    []
  );

  async function submitMass(programId: string) {
    if (!file.value) {
      setFile((f) => ({ ...f, error: JOB_IMPORT_MESSAGES.fileRequired }));
      return;
    }
    cancelUpload();
    // Nova tentativa: o erro anterior sai do campo (volta se esta também falhar).
    setFile((f) => ({ ...f, error: null, retryable: false }));
    const controller = new AbortController();
    uploadAbortRef.current = controller;
    // AC-34 — a Confirmação aparece em carregamento enquanto o backend lê.
    setStep({ kind: 'confirm', loading: true });
    try {
      const prepared = await upload.mutateAsync({
        programId,
        file: file.value,
        signal: controller.signal,
      });
      // Cancelado no meio (Voltar / troca de formato / saída da tela): a
      // resposta tardia não reabre a Confirmação por cima da tela de envio.
      if (uploadAbortRef.current !== controller) return;
      uploadAbortRef.current = null;
      setBatch(prepared);
      setStep({
        kind: 'confirm',
        loading: false,
        summary: { programName: prepared.programName || programName, ...prepared.summary },
        hasConflicts:
          prepared.conflicts.length > 0 ||
          prepared.summary.invalid + prepared.summary.published > 0,
        confirming: false,
        error: null,
      });
    } catch (err) {
      if (uploadAbortRef.current !== controller) return;
      uploadAbortRef.current = null;
      // AC-17/18/19 — fica na tela de envio com a mensagem abaixo do campo.
      setStep({ kind: 'form' });
      setFile((f) => ({
        ...f,
        error: uploadErrorMessage(err),
        retryable: isRetryableUploadError(err),
      }));
    }
  }

  // AC-43 — guarda síncrona do clique duplo (o estado do React chega tarde
  // demais para o segundo clique do mesmo frame).
  const confirmInFlightRef = useRef(false);
  async function handleConfirm() {
    if (!batch || confirmInFlightRef.current) return;
    confirmInFlightRef.current = true;
    const { batchId } = batch;
    setStep((current) =>
      current.kind === 'confirm' && !current.loading
        ? { ...current, confirming: true, error: null }
        : current
    );
    try {
      await confirm.mutateAsync(batchId);
      void queryClient.invalidateQueries({ queryKey: ['recruiter', 'jobs'] });
      setStep((current) =>
        current.kind === 'confirm' && !current.loading ? { kind: 'success' } : current
      );
    } catch (err) {
      // AC-42 — fica na Confirmação com a mensagem; nada pela metade.
      const message = confirmErrorMessage(err);
      setStep((current) =>
        current.kind === 'confirm' && !current.loading
          ? { ...current, confirming: false, error: message }
          : current
      );
    } finally {
      confirmInFlightRef.current = false;
    }
  }

  function handleFieldChange(field: keyof JobCreateValues, value: string) {
    if (field === 'format') {
      // AC-05 — trocar de formato descarta o bloco abandonado; o programa fica.
      cancelUpload();
      form.setFieldValue('name', '');
      form.setFieldValue('contractType', '');
      setFile({ value: null, error: null });
      setNameTaken(null);
    }
    if (field === 'name') setNameTaken(null);
    form.setFieldValue(field, value as never);
  }

  function handleFileSelect(files: FileList | null) {
    const listError = fileListError(files);
    const picked = files?.[0] ?? null;
    if (listError) {
      setFile({ value: null, error: listError });
      return;
    }
    if (!picked) {
      setFile({ value: null, error: null });
      return;
    }
    const localError = localFileError(picked);
    setFile({ value: localError ? null : picked, error: localError });
  }

  async function handleDownloadTemplate() {
    try {
      await downloadImportTemplate();
    } catch {
      toast(GENERIC_ERROR_TOAST, { action: { label: 'Fechar', onClick: () => undefined } });
    }
  }

  async function handleDownloadConflicts() {
    if (!batch) return;
    try {
      await downloadImportConflicts(batch.batchId);
    } catch {
      toast(GENERIC_ERROR_TOAST, { action: { label: 'Fechar', onClick: () => undefined } });
    }
  }

  function goToJobs() {
    void navigate({
      to: '/vagas',
      search: {
        ...DEFAULT_JOBS_SEARCH,
        ...(isValidProgramUuid ? { programId: values.programId } : {}),
      },
    });
  }

  return (
    <JobCreatePageComponent
      values={values}
      errors={{ ...errors, ...(nameTaken ? { name: nameTaken } : {}) }}
      isSubmitting={isSubmitting || postJob.isPending || upload.isPending}
      programOptions={programOptions}
      programsLoading={programsQuery.isPending}
      programsError={programsQuery.isError ? PROGRAMS_LOAD_ERROR_MESSAGE : null}
      contractTypeOptions={contractTypeOptions}
      companyLogoUrl={companyLogoUrl}
      companyName={companyName}
      file={{
        name: file.value?.name ?? null,
        error: file.error,
        retryable: file.retryable === true,
      }}
      step={step}
      onFieldChange={handleFieldChange}
      onFileSelect={handleFileSelect}
      onDownloadTemplate={() => void handleDownloadTemplate()}
      onSubmit={() => void form.handleSubmit()}
      onBack={() => void navigate({ to: '/vagas', search: DEFAULT_JOBS_SEARCH })}
      // AC-35 — Voltar da Confirmação: programa e formato preservados; nada
      // gravado; um envio ainda em curso é abortado.
      onConfirmBack={() => {
        cancelUpload();
        setStep({ kind: 'form' });
      }}
      onConfirm={() => void handleConfirm()}
      onDownloadConflicts={() => void handleDownloadConflicts()}
      onGoToJobs={goToJobs}
    />
  );
}
